import type {
  DecisionActionBlocker,
  DecisionActionSafetyView,
} from "../api/types";
import { ApiError } from "../api";

const ISSUE_CODES = new Set([
  "decision_state_invalid",
  "outer_matched_rule_ids_invalid",
  "outer_review_reasons_invalid",
  "duplicate_matched_rule_id",
  "matched_rule_ids_mismatch",
  "ruleset_version_mismatch",
  "requires_human_review_mismatch",
  "review_reasons_mismatch",
  "canonical_evidence_invalid",
  "parameter_set_mismatch",
  "input_facts_hash_mismatch",
]);
const BLOCKERS = new Set([
  "decision_integrity_failure",
  "request_revision_stale",
  "request_input_stale",
  "code_ruleset_stale",
  "code_ruleset_not_evaluable",
  "parameter_set_stale",
  "rule_params_hash_stale",
  "effective_rules_hash_stale",
  "policy_currentness_not_evaluable",
  "policy_version_stale",
  "post_cutover_read_only",
  "invalid_cutover_marker",
]);

export type DecisionSafetyApiErrorCode =
  | "DECISION_ACTION_BLOCKED"
  | "DECISION_SAFETY_UNAVAILABLE";

function parseSafety(value: unknown): DecisionActionSafetyView | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const safety = value as Partial<DecisionActionSafetyView>;
  if (safety.integrity_status !== "valid" && safety.integrity_status !== "invalid") return null;
  if (!Array.isArray(safety.integrity_issues) || safety.integrity_issues.some(
    (issue) => !issue || typeof issue !== "object" || !ISSUE_CODES.has(issue.code),
  )) return null;
  if (!["current", "code_ruleset_stale", "not_evaluable"].includes(String(safety.code_currentness))) return null;
  if (typeof safety.active_code_ruleset_version !== "string") return null;
  if (![
    "not_applicable",
    "not_evaluable",
    "current",
    "policy_stale",
  ].includes(String(safety.policy_currentness))) return null;
  const policyHasActiveEvidence =
    safety.policy_currentness === "current" || safety.policy_currentness === "policy_stale";
  if (policyHasActiveEvidence) {
    if (
      !Number.isSafeInteger(safety.request_policy_version) ||
      (safety.request_policy_version as number) <= 0 ||
      typeof safety.request_policy_payload_hash !== "string"
    ) return null;
  } else if (
    safety.request_policy_version !== null ||
    safety.request_policy_payload_hash !== null
  ) return null;
  if (!["legacy_write_enabled", "canonical_write_enabled", "post_cutover_read_only", "invalid_marker_read_only"].includes(String(safety.writer_posture))) return null;
  if (!Array.isArray(safety.action_blockers) || safety.action_blockers.some((code) => !BLOCKERS.has(code))) return null;

  const blockerSet = new Set(safety.action_blockers);
  if ((safety.integrity_status === "valid") !== (safety.integrity_issues.length === 0)) return null;
  if ((safety.integrity_status === "invalid") !== blockerSet.has("decision_integrity_failure")) return null;
  if ((safety.code_currentness === "code_ruleset_stale") !== blockerSet.has("code_ruleset_stale")) return null;
  if ((safety.code_currentness === "not_evaluable") !== blockerSet.has("code_ruleset_not_evaluable")) return null;
  const policyUnavailable = blockerSet.has("policy_currentness_not_evaluable");
  const policyStaleBlockers = ["policy_version_stale"] as const;
  const presentPolicyStaleBlockers = policyStaleBlockers.filter((code) => blockerSet.has(code));
  if ((safety.policy_currentness === "not_evaluable") !== policyUnavailable) return null;
  if ((safety.policy_currentness === "policy_stale") !== (presentPolicyStaleBlockers.length > 0)) return null;
  if (
    (safety.policy_currentness === "current" || safety.policy_currentness === "not_applicable") &&
    (policyUnavailable || presentPolicyStaleBlockers.length > 0)
  ) return null;
  if ((safety.writer_posture === "post_cutover_read_only") !== blockerSet.has("post_cutover_read_only")) return null;
  if ((safety.writer_posture === "invalid_marker_read_only") !== blockerSet.has("invalid_cutover_marker")) return null;
  return safety as DecisionActionSafetyView;
}

export function decisionActionSafetyModel(value: unknown) {
  const safety = parseSafety(value);
  if (!safety) {
    return {
      evidenceValid: false,
      decisionActionsBlocked: true,
      writerActionsBlocked: true,
      reasonKeys: ["ws.decisionSafetyUnavailable"],
      actionBlockers: [] as DecisionActionBlocker[],
    };
  }
  const reasonKeys: string[] = [];
  for (const blocker of safety.action_blockers) {
    if (blocker === "decision_integrity_failure") {
      reasonKeys.push("ws.decisionSafety.integrity");
      for (const issue of safety.integrity_issues) {
        reasonKeys.push(`ws.decisionSafety.issue.${issue.code}`);
      }
    } else {
      reasonKeys.push(`ws.decisionSafety.${blocker}`);
    }
  }
  return {
    evidenceValid: true,
    decisionActionsBlocked: safety.action_blockers.length > 0,
    writerActionsBlocked:
      safety.writer_posture !== "legacy_write_enabled" &&
      safety.writer_posture !== "canonical_write_enabled",
    reasonKeys,
    actionBlockers: [...safety.action_blockers],
  };
}

export function decisionSafetyApiErrorCode(error: unknown): DecisionSafetyApiErrorCode | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== "object") return null;
  const code = (error.data as { code?: unknown }).code;
  if (error.status === 409 && code === "DECISION_ACTION_BLOCKED") return code;
  if (error.status === 503 && code === "DECISION_SAFETY_UNAVAILABLE") return code;
  return null;
}
